import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button, Callout, Input, Modal, theme } from '@agentisai/design-system';
import { MAX_WS_TITLE_LEN, RenameWorkstreamRequestSchema } from '@agentisai/shared-types';
import { useRenameWorkstream } from '../queries/useRenameWorkstream';

const errorSlot = { marginTop: theme.space[3] } as const;

export interface RenameWorkstreamDialogProps {
  /** The workstream to rename — keys the mutation + its list invalidation. */
  readonly workstreamId: string;
  /** The current title — prefills the field. */
  readonly title: string;
  /** Dismiss (Cancel / Escape / backdrop) or, after a successful save, close. */
  readonly onClose: () => void;
}

/**
 * RenameWorkstreamDialog — a DS `Modal` with one field, opened from the rail
 * card's Rename link. Validates against the SAME shared schema the BFF enforces
 * (`RenameWorkstreamRequestSchema`: trimmed, 1–{@link MAX_WS_TITLE_LEN} chars),
 * so the client and server can never disagree about what is a valid title.
 *
 * Save is disabled while the draft is invalid, unchanged, or in flight (the
 * double-submit guard, frontend-architecture R7), and the dialog is
 * non-dismissible mid-flight. A failure keeps it open with an inline retry
 * message; the title is left untouched server-side, so a retry is safe. On
 * success `useRenameWorkstream` invalidates the workstream list and the dialog
 * closes — `useDialog` restores focus to the card's Rename link.
 */
export function RenameWorkstreamDialog({
  workstreamId,
  title,
  onClose,
}: RenameWorkstreamDialogProps) {
  const [draft, setDraft] = useState(title);
  // The Modal's `useDialog` lands focus on the first focusable — the ✕ — so put
  // it (and the current name, selected, ready to overtype) in the field instead.
  // This effect lives in the Modal's parent, so it runs AFTER the dialog's own
  // and wins. Imperative focus is the sanctioned effect+ref use (R24/R25).
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  const mutation = useRenameWorkstream(workstreamId);
  const pending = mutation.isPending;

  const parsed = RenameWorkstreamRequestSchema.safeParse({ title: draft });
  const unchanged = parsed.success && parsed.data.title === title;
  // Only nag about an over-long name — an empty field just leaves Save disabled.
  const tooLong = draft.trim().length > MAX_WS_TITLE_LEN;
  const canSave = parsed.success && !unchanged && !pending;

  function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!parsed.success || unchanged || pending) return;
    mutation.mutate(parsed.data.title, { onSuccess: onClose });
  }

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!pending}
      size="sm"
      title="Rename workstream"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => submit()} disabled={!canSave}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      {/* A real <form> so Enter saves; the Save button lives in the Modal's
          actions slot, outside it, so Enter is routed to `submit` here. */}
      <form onSubmit={submit}>
        <Input
          label="Workstream name"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          ref={inputRef}
          fullWidth
          disabled={pending}
          error={tooLong ? `Keep it under ${MAX_WS_TITLE_LEN} characters.` : undefined}
        />
      </form>
      {mutation.isError && (
        <div role="alert" style={errorSlot}>
          <Callout tone="danger">Couldn’t rename this workstream. Please try again.</Callout>
        </div>
      )}
    </Modal>
  );
}
RenameWorkstreamDialog.displayName = 'RenameWorkstreamDialog';
